import {
  DIRECTION_DELTA,
  isMazeCellOpen,
  isMazePathOpen,
  turnLeftDirection,
  turnRightDirection,
  type MazeStageDefinition,
} from '../../flow/config/mazeStages';
import type { MazeRobotPose } from '../../flow/animation/useWegoAnimationScene';
import { getWalkingMotionDistanceCells, type WalkingDisplacement } from '../../flow/config/walkingMotions';

export type MazeWalkingResult = {
  pose: MazeRobotPose;
  blocked: boolean;
  goalReached: boolean;
};

export const TURN_FORWARD_DISTANCE_CELLS = 0.34;
const CELL_SNAP_TOLERANCE = 0.31;

export const isMazeGoalPosition = (
  stage: MazeStageDefinition,
  pose: Pick<MazeRobotPose, 'x' | 'y'>,
): boolean => stage.goal != null
  && Math.hypot(pose.x - stage.goal.x, pose.y - stage.goal.y) <= CELL_SNAP_TOLERANCE;

export const applyMazeWalkingMotion = (
  stage: MazeStageDefinition,
  current: MazeRobotPose,
  motionNo: number,
  displacement?: WalkingDisplacement,
): MazeWalkingResult => {
  if (motionNo === 8 || motionNo === 9) {
    const direction = motionNo === 8
      ? turnRightDirection(current.dir)
      : turnLeftDirection(current.dir);
    const delta = DIRECTION_DELTA[direction];
    const target = {
      x: current.x + delta.x * TURN_FORWARD_DISTANCE_CELLS,
      y: current.y + delta.y * TURN_FORWARD_DISTANCE_CELLS,
    };
    if (!isMazePathOpen(stage, current, target)) {
      return { pose: current, blocked: true, goalReached: false };
    }
    return {
      pose: {
        ...target,
        dir: direction,
      },
      blocked: false,
      goalReached: false,
    };
  }

  const direction = motionNo === 5 || motionNo === 106
    ? ((current.dir + 4) % 8) as typeof current.dir
    : current.dir;
  // Curves end on the nearest 45° heading (right turns increase dir); travel is measured from the start heading.
  const turns = Math.round(-(displacement?.yaw ?? 0) / 45);
  const finalDir = ((current.dir + turns + 8 * Math.ceil(Math.abs(turns) / 8)) % 8) as typeof current.dir;
  const delta = DIRECTION_DELTA[direction];
  const distance = displacement?.forward ?? getWalkingMotionDistanceCells(motionNo);
  const lateral = displacement?.lateral ?? 0;
  const target = {
    x: current.x + delta.x * distance + delta.y * lateral,
    y: current.y + delta.y * distance - delta.x * lateral,
  };
  if (displacement?.path) {
    let previous = current;
    for (const point of displacement.path) {
      const next = {
        x: current.x + delta.x * point.forward + delta.y * point.lateral,
        y: current.y + delta.y * point.forward - delta.x * point.lateral,
        dir: current.dir,
      };
      if (!isMazePathOpen(stage, previous, next)) {
        return { pose: current, blocked: true, goalReached: false };
      }
      previous = next;
    }
  }
  if (!isMazeCellOpen(stage, target) || !isMazePathOpen(stage, current, target)) {
    return { pose: current, blocked: true, goalReached: false };
  }
  return {
    pose: { x: target.x, y: target.y, dir: finalDir },
    blocked: false,
    goalReached: isMazeGoalPosition(stage, target),
  };
};